/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */

// This file remains unchanged. The unit tests will be created in a new file.
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { setOwner } from '@ember/application';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type RouterService from '@ember/routing/router-service';
import { action } from '@ember/object';
import ENV from 'embercom/config/environment';
import { cancel, later } from '@ember/runloop';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { type EmberRunTimer } from '@ember/runloop/types';
import { AI_AGENT_ENTITY_TYPES } from 'embercom/lib/knowledge-hub/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import type IntlService from 'ember-intl/services/intl';
import type { StandalonePlatform } from 'embercom/objects/standalone/constants';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SuiteMigrationService from 'embercom/services/suite-migration-service';
import type InboxApi from 'embercom/services/inbox-api';

export interface NavItemInputs {
  route: string;
  activeRoutes?: string[];
  icon?: InterfaceIconName;
  labelIntlKey: string;
  isLive?: () => boolean;
  platforms?: StandalonePlatform[];
  requiredFeatures?: string[];
  excludedFeatures?: string[];
  children?: NavItemInputs[];
  urlSegments?: Record<string, string>;
  customOnClick?: () => void;
  hasBetaBadge?: boolean;
}

export class NavTree {
  @tracked items: NavItem[] = [];

  @tracked forcedActiveItem?: NavItem;

  @service declare router: RouterService;

  constructor(owner: unknown) {
    setOwner(this, owner);
  }

  activeItemTimeout?: EmberRunTimer;

  routeDidChangeCallback?: () => void;

  setForcedActiveItem(item: NavItem) {
    this.forcedActiveItem = item;

    if (this.activeItemTimeout) {
      cancel(this.activeItemTimeout);
    }

    // This is a failsafe to ensure that the forced active item does not remain active forver
    // E.g. in cases where we've redirected to a different route than what the nav item specified.
    this.activeItemTimeout = later(() => {
      this.forcedActiveItem = undefined;
    }, ENV.APP._10000MS);

    if (this.routeDidChangeCallback) {
      this.router.off('routeDidChange', this.routeDidChangeCallback);
    }

    // We listen to the router to know when we've actually reached the route that the nav item specifies.
    // If we've reached that route, we unset the forced active item.
    // We listen for the route changes as the Ember router will go through several interstitial states
    // before reaching the final destination and we want the nav item to remain selected in the UI through those states.
    this.routeDidChangeCallback = () => {
      if (
        this.forcedActiveItem?.route &&
        this.router.currentRouteName.startsWith(this.forcedActiveItem.route)
      ) {
        this.forcedActiveItem = undefined;
      }
    };

    this.router.on('routeDidChange', this.routeDidChangeCallback);
  }

  get allItems() {
    return this.items.flatMap((item) => item.allItems);
  }

  addItem(item: NavItem) {
    this.items.pushObject(item);
  }
}

export class NavItem {
  @service declare appService: {
    app: {
      id: string;
      canUseFeature: (feature: string) => boolean;
      hasStandalonePlatform: (platformName: StandalonePlatform) => boolean;
    };
  };
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare router: RouterService;
  @service declare contentImportService: any;
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare suiteMigrationService: SuiteMigrationService;
  @service declare inboxApi: InboxApi;
  root: NavTree;
  parent?: NavItem;
  route: string;
  activeRoutes: string[];
  icon?: InterfaceIconName;
  labelIntlKey: string;
  platforms: StandalonePlatform[];
  requiredFeatures: string[];
  excludedFeatures: string[];
  children: NavItem[];
  urlSegments?: Record<string, string>;
  isLive?: () => boolean;
  customOnClick?: () => void;
  hasBetaBadge: boolean;

  constructor(root: NavTree, owner: unknown, inputs: NavItemInputs, parent?: NavItem) {
    setOwner(this, owner);
    this.root = root;
    this.route = inputs.route;
    this.activeRoutes = [inputs.route, ...(inputs.activeRoutes ?? [])];
    this.icon = inputs.icon;
    this.labelIntlKey = inputs.labelIntlKey;
    this.parent = parent;
    this.platforms = inputs.platforms ?? [];
    this.requiredFeatures = inputs.requiredFeatures ?? [];
    this.excludedFeatures = inputs.excludedFeatures ?? [];
    this.root.addItem(this);
    this.children = inputs.children?.map((child) => new NavItem(root, owner, child, this)) ?? [];
    this.urlSegments = inputs.urlSegments;
    this.customOnClick = inputs.customOnClick?.bind(this);
    this.isLive = inputs.isLive;
    this.hasBetaBadge = inputs.hasBetaBadge ?? false;
  }

  get routeWithParams() {
    if (!this.urlSegments) {
      return this.route;
    }
    return `${this.route}?${new URLSearchParams(this.urlSegments).toString()}`;
  }

  get getIsLive(): boolean {
    return this.isLive?.() ?? false;
  }

  get hasStatusBadge(): boolean {
    return this.isLive !== undefined;
  }

  get allItems(): Array<NavItem> {
    return [this, ...this.children.flatMap((child) => child.allItems)];
  }

  get tooltip() {
    if (
      this.route !== 'apps.app.knowledge-hub.all-content' ||
      !this.knowledgeHubService.usageSummary
    ) {
      return undefined;
    }
    return this.intl.t('standalone.navbar.navbar-link.count-tooltip');
  }

  get count() {
    if (this.route === 'apps.app.standalone.knowledge.suggestions') {
      return this.contentImportService.aiContentReviewSummary.pending;
    }
    if (
      this.route !== 'apps.app.knowledge-hub.all-content' ||
      !this.knowledgeHubService.usageSummary
    ) {
      return undefined;
    }
    let entityTypesForCount = AI_AGENT_ENTITY_TYPES.reject((type) => type === EntityType.Answer);
    let filteredSummary = Object.values(this.knowledgeHubService.usageSummary).filter((summary) =>
      entityTypesForCount.includes(summary.entity_type),
    );
    return filteredSummary.reduce((acc, summary) => (acc += summary.agent), 0);
  }

  @action onClick() {
    if (this.customOnClick) {
      this.customOnClick();
    } else if (this.eligibleChildren.length === 0) {
      this.root.setForcedActiveItem(this);

      // Handle routes with dynamic segments (like :platform/:channel)
      if (this.urlSegments && Object.keys(this.urlSegments).length > 0) {
        let segmentValues = Object.values(this.urlSegments) as [];
        this.router.transitionTo(this.route, this.app.id, ...segmentValues);
      } else {
        this.router.transitionTo(this.route, this.app.id);
      }
    } else {
      this.eligibleChildren[0].onClick();
    }
  }

  get eligibleChildren() {
    // first items with green live status, then orange not live, then items without any status
    let sortedChildren = this.children
      .filter((child) => child.meetsRequirements)
      .slice()
      .sort((a, b) => {
        function isLiveSortValue(child: NavItem) {
          if (child.isLive?.() === true) {
            return 0;
          }
          if (child.isLive?.() === false) {
            return 1;
          }
          return 2;
        }
        return isLiveSortValue(a) - isLiveSortValue(b);
      });
    return sortedChildren;
  }

  get isFirstChild() {
    return this.parent?.eligibleChildren[0] === this;
  }

  get isLastChild() {
    return this.parent?.eligibleChildren[this.parent.eligibleChildren.length - 1] === this;
  }

  get app() {
    return this.appService.app;
  }

  get satisfiesPlaformRequirements() {
    if (this.platforms.length === 0) {
      return true;
    }

    return this.platforms.any((platform) => this.app.hasStandalonePlatform(platform));
  }

  get meetsRequirements() {
    if (!this.satisfiesPlaformRequirements) {
      return false;
    }

    if (this.requiredFeatures.length === 0 && this.excludedFeatures.length === 0) {
      return true;
    }

    let passesRequiredFeatures = true;
    let passesExcludedFeatures = true;

    if (this.requiredFeatures.length > 0) {
      passesRequiredFeatures = this.requiredFeatures.every((feature) =>
        this.app.canUseFeature(feature),
      );
    }

    if (this.excludedFeatures.length > 0) {
      passesExcludedFeatures = this.excludedFeatures.every(
        (feature) => !this.app.canUseFeature(feature),
      );
    }

    return passesRequiredFeatures && passesExcludedFeatures;
  }

  paramsMatch(
    params1: Record<string, string | undefined>,
    params2: Record<string, string | undefined>,
  ): boolean {
    if (!params1 || !params2) {
      return false;
    }

    let allKeys = new Set([...Object.keys(params1), ...Object.keys(params2)]);

    for (let key of allKeys) {
      if (params1[key] !== params2[key]) {
        return false;
      }
    }

    return true;
  }

  get isActive() {
    if (this.root.forcedActiveItem) {
      return this.root.forcedActiveItem === this;
    }
    // This is pretty nasty but it's the simpliest way to get child routes to display as active when the route is dynamic
    // and contains params instead of a full route
    if (this.urlSegments) {
      return (
        this.paramsMatch(this.router?.currentRoute?.params, this.urlSegments) ||
        // For the workflow show route, we need to check the parent params as well
        this.paramsMatch(this.router?.currentRoute?.parent?.params ?? {}, this.urlSegments)
      );
    } else {
      return this.activeRoutes.some((route) => this.router.currentRouteName.includes(route));
    }
  }

  get hasActiveChild() {
    return this.eligibleChildren.some((child) => child.isActive);
  }
}
